import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { ContractInput } from './types';

export const contractKeys = {
  all: ['contracts'] as const,
  lists: () => [...contractKeys.all, 'list'] as const,
  list: (params: api.ContractListParams) => [...contractKeys.lists(), params] as const,
  detail: (id: string | number) => [...contractKeys.all, 'detail', id] as const,
};

export function useContracts(params: api.ContractListParams) {
  return useQuery({
    queryKey: contractKeys.list(params),
    queryFn: () => api.listContracts(params),
    placeholderData: keepPreviousData,
  });
}

export function useContract(id: string | number) {
  return useQuery({
    queryKey: contractKeys.detail(id),
    queryFn: () => api.getContract(id),
    enabled: Boolean(id),
  });
}

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContractInput) => api.createContract(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contractKeys.lists() });
      toast.success('Contract created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateContract(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<ContractInput>) => api.updateContract(id, input),
    onSuccess: (contract) => {
      qc.invalidateQueries({ queryKey: contractKeys.lists() });
      qc.setQueryData(contractKeys.detail(id), contract);
      toast.success('Contract updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useArchiveContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.archiveContract(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contractKeys.lists() });
      toast.success('Contract archived');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRestoreContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.restoreContract(id),
    onSuccess: (contract) => {
      qc.invalidateQueries({ queryKey: contractKeys.lists() });
      qc.setQueryData(contractKeys.detail(contract.public_id), contract);
      toast.success('Contract restored');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function usePurgeContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.purgeContract(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contractKeys.lists() });
      toast.success('Contract permanently deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

/**
 * Sign the contract → backend provisions a Project (returned), wins the deal,
 * and accepts the parent proposal. Invalidates contracts, deals, and projects.
 */
export function useSignContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, project_name }: { id: string | number; project_name?: string }) =>
      api.signContract(id, { project_name }),
    onSuccess: (_project, { id }) => {
      qc.invalidateQueries({ queryKey: contractKeys.lists() });
      qc.invalidateQueries({ queryKey: contractKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Contract signed — project created');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
